///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { EntityCameraAdapter } from "@/earthbrowser/datasets/EntityCameraAdapter"
import { type InfoPanelData, type InfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { geographic } from "@/earthbrowser/utils/geographic"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import type { Camera } from "@stovokor/camera/Camera"
import { type RenderActionSet } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type FocusTransitionConfig } from "@stovokor/gestures/FocusTarget"
import { GestureNode } from "@stovokor/gestures/GestureNode"
import { type GestureHitResult } from "@stovokor/gestures/GestureRecognizer"
import { vec3 } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type DatasetSceneObject } from "../DatasetLayer"
import { parseEnsembleTracks, parseTrack, parseWatchWarnings, parseWindProbability, parseWindRadii, type StormProperties, type StormTrackPoint } from "./HurricanesTable"
import { StormConeNode, type StormConeGeoJSON } from "./StormConeNode"
import { StormEnsembleTracksNode } from "./StormEnsembleTracksNode"
import { StormForecastPointsNode } from "./StormForecastPointsNode"
import { StormLandfallNode } from "./StormLandfallNode"
import { StormMarkerNode } from "./StormMarkerNode"
import { StormTrackNode } from "./StormTrackNode"
import { StormWatchWarningNode } from "./StormWatchWarningNode"
import { StormWindFieldNode } from "./StormWindFieldNode"
import { StormWindProbabilityNode } from "./StormWindProbabilityNode"
import { type WindProbabilityThreshold } from "./windProbability"
import { combinedTimeline, findLandfall, stormStateAtTime } from "./stormTrack"

const cStormModelAltitude = 2.0
const cKmPerDegree = 111.2

// Cone and track drape altitude: clearance above the highest sampled
// terrain, since cones regularly cross coastal mountain ranges
const cDrapeClearanceKm = 1.5
// Max terrain samples along a cone ring when finding its high point
const cMaxRingElevationSamples = 24

// Camera framing: storms are enormous, so cap how far focus pulls back
const cStormFocusMinDistance = 400
const cStormFocusMaxDistance = 4500
const cStormFocusMaxZoomRadius = 6000

// Hit test: the marker plus a generous grab radius; the cone itself is
// deliberately not a click target (it can cover an entire ocean basin)
const cStormHitRadiusKm = 120

// Reposition along the track when the clock moved by more than this
const cClockStepMs = 60_000

export class StormFeature extends GestureNode implements DatasetSceneObject<StormProperties>, InfoPanelProvider {
	public readonly focusable: boolean = true
	public readonly id: string

	public feature: StormProperties
	public coordinate: geographic

	private readonly cameraAdapter: EntityCameraAdapter
	private readonly featureContext: EarthBrowserContext

	private readonly marker: StormMarkerNode
	private trackNode?: StormTrackNode
	private ensembleNode?: StormEnsembleTracksNode
	private coneNode?: StormConeNode
	private forecastPointsNode?: StormForecastPointsNode
	private watchWarningNode?: StormWatchWarningNode
	private windFieldNode?: StormWindFieldNode
	private windProbabilityNode?: StormWindProbabilityNode
	private windProbabilityThreshold: WindProbabilityThreshold = 34
	private landfallNode?: StormLandfallNode
	private hitSphere = new sphere(cStormHitRadiusKm)

	/** Combined observed + forecast timeline for clock interpolation */
	private timeline: StormTrackPoint[] = []
	private lastClockMs = 0
	/** Half the storm bbox diagonal in km, used for camera framing */
	private extentRadiusKm: number
	/** False until terrain elevation was applied with a loaded elevation layer */
	private elevationResolved = false
	private terrainVersion = -1
	private focused = false
	private emphasized = false
	private columnOpen = false

	constructor(context: EarthBrowserContext, feature: StormProperties) {
		super(context)
		this.id = feature.id
		this.feature = feature
		this.featureContext = context

		this.coordinate = new geographic(
			feature.lon * trig.rad,
			feature.lat * trig.rad,
			cStormModelAltitude
		)

		this.rebuildTimeline()
		this.placeAt(feature.lat, feature.lon)

		this.extentRadiusKm = this.computeExtentRadiusKm(feature)

		this.marker = new StormMarkerNode(context, {
			category: feature.category,
			windKt: feature.max_wind_kt,
			latitude: feature.lat,
		})
		this.addNode(this.marker)

		this.buildOverlays()

		const focusDistance = Math.min(
			cStormFocusMaxDistance,
			Math.max(cStormFocusMinDistance, this.extentRadiusKm * 1.6)
		)
		this.cameraAdapter = new EntityCameraAdapter(context, this, focusDistance, cStormFocusMaxZoomRadius)
	}

	private rebuildTimeline(): void {
		this.timeline = combinedTimeline(
			parseTrack(this.feature.observed_track),
			parseTrack(this.feature.forecast_track)
		)
	}

	/**
	 * Place the feature at a lat/lon on the terrain (storms are usually over
	 * ocean, but landfalling centers cross real elevation). The track and
	 * cone children carry absolute world-space geometry, so they are
	 * re-parented with the inverse transform whenever the center moves.
	 */
	private placeAt(latDeg: number, lonDeg: number): void {
		const lat = latDeg * trig.rad
		const lon = lonDeg * trig.rad
		const elevationKm = this.featureContext.displayElevationKm(new geographic(lon, lat, 0))
		this.coordinate = new geographic(lon, lat, elevationKm + cStormModelAltitude)

		const xRot = matrix.axisRotation(vec3.unitX, lat + trig.half_pi)
		const yRot = matrix.axisRotation(vec3.unitY, lon + trig.pi)
		this.transform = matrix.translate(this.coordinate.cartesian)
			.premultiply(yRot.premultiply(xRot))

		const inverse = this.transform.invert()
		for (const node of this.worldSpaceOverlays()) {
			node.transform = inverse
		}

		this.elevationResolved = this.featureContext.baseElevation.loaded
		this.terrainVersion = this.featureContext.terrainVersion
	}

	/** Overlay children carrying absolute world-space geometry */
	private worldSpaceOverlays(): Array<StormTrackNode | StormEnsembleTracksNode | StormConeNode | StormForecastPointsNode | StormWatchWarningNode | StormWindFieldNode | StormWindProbabilityNode | StormLandfallNode> {
		const nodes: Array<StormTrackNode | StormEnsembleTracksNode | StormConeNode | StormForecastPointsNode | StormWatchWarningNode | StormWindFieldNode | StormWindProbabilityNode | StormLandfallNode> = []
		// Probability swath and ensemble first so the official track and cone draw over them
		if (this.windProbabilityNode) nodes.push(this.windProbabilityNode)
		if (this.ensembleNode) nodes.push(this.ensembleNode)
		if (this.trackNode) nodes.push(this.trackNode)
		if (this.coneNode) nodes.push(this.coneNode)
		if (this.forecastPointsNode) nodes.push(this.forecastPointsNode)
		if (this.watchWarningNode) nodes.push(this.watchWarningNode)
		if (this.windFieldNode) nodes.push(this.windFieldNode)
		if (this.landfallNode) nodes.push(this.landfallNode)
		return nodes
	}

	/**
	 * Highest terrain (km) sampled along the cone's outer rings, so the
	 * draped fill clears coastal mountains the cone crosses
	 */
	private maxTerrainElevationKm(geometry: StormConeGeoJSON | null): number {
		const elevation = this.featureContext.baseElevation
		const exaggeration = this.featureContext.terrainExaggeration
		let maxMeters = 0

		const polygons: number[][][][] =
			geometry?.type === "Polygon" ? [geometry.coordinates]
				: geometry?.type === "MultiPolygon" ? geometry.coordinates
					: []
		for (const rings of polygons) {
			const outer = rings[0]
			if (!outer) continue
			const step = Math.max(1, Math.floor(outer.length / cMaxRingElevationSamples))
			for (let i = 0; i < outer.length; i += step) {
				const [lon, lat] = outer[i]
				const meters = elevation.getElevation(new geographic(lon * trig.rad, lat * trig.rad, 0))
				if (meters > maxMeters) {
					maxMeters = meters
				}
			}
		}
		return maxMeters > 0 ? maxMeters / 1000 * exaggeration : 0
	}

	private computeExtentRadiusKm(feature: StormProperties): number {
		const midLat = (feature.bbox_min_lat + feature.bbox_max_lat) / 2
		const dLatKm = (feature.bbox_max_lat - feature.bbox_min_lat) * cKmPerDegree
		const dLonKm = (feature.bbox_max_lon - feature.bbox_min_lon) * cKmPerDegree * Math.cos(midLat * trig.rad)
		return Math.max(Math.sqrt(dLatKm * dLatKm + dLonKm * dLonKm) / 2, 100)
	}

	private parseCone(): StormConeGeoJSON | null {
		if (!this.feature.cone) return null
		try {
			const geometry = JSON.parse(this.feature.cone) as StormConeGeoJSON
			if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") return null
			return geometry
		} catch {
			return null
		}
	}

	/**
	 * Overlay vertices are absolute world-space (they span too many degrees
	 * for a single local frame), so every overlay node is parented with the
	 * inverse of this feature's transform.
	 */
	private buildOverlays(): void {
		const cone = this.parseCone()
		const drapeAltitude = this.maxTerrainElevationKm(cone) + cDrapeClearanceKm
		const inverse = this.transform.invert()

		const observed = parseTrack(this.feature.observed_track)
		const forecast = parseTrack(this.feature.forecast_track)
		if (observed.length + forecast.length >= 2) {
			this.trackNode = new StormTrackNode(this.featureContext, observed, forecast, drapeAltitude)
		}

		// ECMWF ensemble spaghetti underneath the official track
		const ensemble = parseEnsembleTracks(this.feature.ensemble_tracks)
		if (ensemble && ensemble.members.length > 0) {
			this.ensembleNode = new StormEnsembleTracksNode(this.featureContext, ensemble, drapeAltitude)
		}

		if (cone) {
			this.coneNode = new StormConeNode(this.featureContext, cone, drapeAltitude)
		}

		// NHC wind speed probabilities under the cone (NHC basins only)
		const windProbability = parseWindProbability(this.feature.wind_probability)
		if (windProbability) {
			this.windProbabilityNode = new StormWindProbabilityNode(this.featureContext, windProbability, drapeAltitude)
			this.windProbabilityNode.setThreshold(this.windProbabilityThreshold)
		}

		if (forecast.length > 0) {
			this.forecastPointsNode = new StormForecastPointsNode(this.featureContext, forecast, drapeAltitude)
		}

		const watchWarnings = parseWatchWarnings(this.feature.watches_warnings)
		if (watchWarnings.length > 0) {
			this.watchWarningNode = new StormWatchWarningNode(this.featureContext, watchWarnings, drapeAltitude)
		}

		const windRadii = parseWindRadii(this.feature.wind_radii)
		if (windRadii) {
			this.windFieldNode = new StormWindFieldNode(
				this.featureContext, windRadii, this.feature.lat, this.feature.lon, drapeAltitude
			)
		}

		// Landfall is derived by sampling terrain, so it needs real elevation
		// data; before the elevation layer loads everything reads as sea
		if (this.featureContext.baseElevation.loaded && forecast.length >= 2) {
			const elevation = this.featureContext.baseElevation
			const landfall = findLandfall(forecast, (lonRad, latRad) =>
				elevation.getElevation(new geographic(lonRad, latRad, 0)))
			if (landfall) {
				this.landfallNode = new StormLandfallNode(this.featureContext, landfall, drapeAltitude)
			}
		}

		for (const node of this.worldSpaceOverlays()) {
			node.transform = inverse
			this.addNode(node)
		}

		this.applyOverlayFocus()
	}

	private rebuildOverlays(): void {
		for (const node of this.worldSpaceOverlays()) {
			this.removeNode(node)
			node.destroy()
		}
		this.trackNode = undefined
		this.ensembleNode = undefined
		this.coneNode = undefined
		this.forecastPointsNode = undefined
		this.watchWarningNode = undefined
		this.windFieldNode = undefined
		this.windProbabilityNode = undefined
		this.landfallNode = undefined

		// Preserve the selected styling across the rebuild
		this.buildOverlays()
	}

	/**
	 * While the storm column floats over this storm its marker and wind
	 * rings are hidden (the block carries both); the track and cone stay so
	 * the block reads against the forecast on the globe
	 */
	public setColumnOpen(open: boolean): void {
		this.columnOpen = open
		this.marker.visible = !open
		if (this.windFieldNode) this.windFieldNode.visible = !open
	}

	/** Which wind threshold the probability swath shows (the storm column HUD switches it) */
	public setWindProbabilityThreshold(kt: WindProbabilityThreshold): void {
		this.windProbabilityThreshold = kt
		this.windProbabilityNode?.setThreshold(kt)
	}

	/**
	 * Selected-storm presentation without camera focus: the video render
	 * mode turns it on for the pull-back shot so the forecast cone, track
	 * and labeled forecast points carry the "what happens next" beat
	 */
	public setEmphasis(on: boolean): void {
		if (this.emphasized === on) return
		this.emphasized = on
		this.applyOverlayFocus()
	}

	/** Apply focus-dependent styling and visibility to the overlay nodes */
	private applyOverlayFocus(): void {
		const selected = this.focused || this.emphasized
		this.trackNode?.setFocused(selected)
		this.ensembleNode?.setFocused(selected)
		this.coneNode?.setFocused(selected)
		this.windProbabilityNode?.setFocused(selected)
		this.watchWarningNode?.setFocused(selected)
		// Time labels and the landfall estimate only show for the selected
		// storm so a basin with several storms doesn't become a text cloud
		this.forecastPointsNode?.setLabelsVisible(selected)
		if (this.landfallNode) this.landfallNode.visible = selected
		// Overlays are rebuilt on advisory updates; keep the column's hiding
		if (this.windFieldNode) this.windFieldNode.visible = !this.columnOpen
	}

	/**
	 * Per-frame maintenance:
	 * - Re-drape geometry once the async elevation layer finishes loading
	 * - Follow the app clock: interpolate the storm center along its
	 *   observed + forecast timeline so scrubbing time moves the storm,
	 *   updating the marker's intensity styling as it travels
	 */
	override render(state: RenderState): RenderActionSet[] {
		if ((!this.elevationResolved || this.terrainVersion !== this.featureContext.terrainVersion) && this.featureContext.baseElevation.loaded) {
			this.placeAt(this.coordinate.latitude * trig.deg, this.coordinate.longitude * trig.deg)
			this.rebuildOverlays()
		}

		const clockMs = this.featureContext.clock.clockTime.date.getTime()
		if (Math.abs(clockMs - this.lastClockMs) >= cClockStepMs && this.timeline.length > 0) {
			this.lastClockMs = clockMs
			const stormState = stormStateAtTime(this.timeline, clockMs)
			if (stormState) {
				this.placeAt(stormState.lat, stormState.lon)
				this.marker.updateStyle({
					category: stormState.category,
					windKt: stormState.wind_kt,
					latitude: stormState.lat,
				})
			}
		}

		return super.render(state)
	}

	/** Called on every dataset poll; rebuilds track/cone when the advisory changed */
	public updateFeature(feature: StormProperties): void {
		const changed = feature.update_key !== this.feature.update_key
		this.feature = feature

		if (changed) {
			this.rebuildTimeline()
			this.extentRadiusKm = this.computeExtentRadiusKm(feature)
			// Force the next render to re-evaluate the clock position
			this.lastClockMs = 0
			this.rebuildOverlays()

			// The info panel snapshots the storm into redux at focus time, so
			// push the fresh advisory to it. This also covers the deep-link
			// race where focus lands before the first dataset sync finishes.
			if (this.focused) {
				const panelData = this.getInfoPanelData()
				engineBridge.dispatch({
					type: engineActions.openInfoWindow,
					payload: {
						title: panelData.title,
						subtitle: panelData.subtitle,
						theme: panelData.theme,
						contentType: panelData.contentType,
						contentData: panelData.contentData,
					}
				})
			}
		}
	}

	// ========== InfoPanelProvider ==========

	public getInfoPanelData(): InfoPanelData {
		return {
			title: `${this.feature.classification} ${this.feature.name}`,
			subtitle: basinName(this.feature.basin),
			theme: "hurricane",
			contentType: "hurricane",
			contentData: {
				type: "hurricane",
				storm: this.feature
			}
		}
	}

	// ========== Focus Lifecycle ==========

	public onFocusGained(camera: Camera): void {
		this.focused = true
		this.cameraAdapter.attach(camera)
		this.marker.setFocused(true)
		this.applyOverlayFocus()

		if (this.feature.id) {
			pushTargetToHistory('storm', String(this.feature.id))
		}
	}

	public onFocusLost(): void {
		this.focused = false
		this.cameraAdapter.detach()
		this.marker.setFocused(false)
		this.applyOverlayFocus()
	}

	public onFocusUpdate(): void {
		this.cameraAdapter.update()
	}

	public focusTransition(camera: Camera): FocusTransitionConfig {
		return {
			target: () => this.cameraAdapter.getTargetCameraState(camera),
			params: {
				duration: 1,
				easing: "easeInOut"
			}
		}
	}

	// ========== Command Handlers ==========

	public handleCommand(command: string, data?: unknown): boolean {
		return this.cameraAdapter.handleCommand(command, data)
	}

	override gestureIntersection(state: RenderState): GestureHitResult | undefined {
		// Inflate the hit sphere the same way the marker inflates on screen,
		// so far-away storms rendered at their minimum apparent size stay
		// clickable. The cone is deliberately not part of the hit volume.
		const cameraDistance = state.camera.position.subtract(this.transform.position).length
		const effectiveRadius = Math.max(
			this.marker.radius,
			cStormHitRadiusKm,
			cameraDistance * this.marker.minAngularSize
		)
		if (effectiveRadius !== this.hitSphere.radius) {
			this.hitSphere = new sphere(effectiveRadius)
		}

		const dist = this.hitSphere.intersect(state.ray)
		if (dist > 0) {
			const hitPoint = state.ray.pointAt(dist)
			return { node: this, hitPoint: hitPoint, distance: dist }
		}
		return undefined
	}
}

/** Human-readable basin name for panel subtitles */
export function basinName(basin: string): string {
	switch (basin.toUpperCase()) {
		case "AL": return "Atlantic"
		case "EP": return "Eastern Pacific"
		case "CP": return "Central Pacific"
		case "WP": return "Western Pacific"
		case "IO": return "Indian Ocean"
		case "SH": return "Southern Hemisphere"
		default: return basin
	}
}
